import { useTranslations } from 'next-intl';
import { type FC, useCallback, useLayoutEffect } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import clsx from 'clsx';
import { z } from 'zod';

import { FormSelect } from '@/components/common/form/FormSelect';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { CurrencySelect } from '@/components/common/select/CurrencySelect';
import { type GateGroupOption, GateGroupSelect } from '@/components/common/select/GateGroupSelect';
import { OperationPeriodSelect } from '@/components/common/select/OperationPeriodSelect';
import { type WithClassName } from '@/types/react';

import {
  DashboardV2PeriodType,
  StatusChartOperationType,
} from '../../../../../../../../../../../../../generated/graphql';

import styles from './StatusChartFilter.module.scss';

type StatusChartFilterProps = WithClassName & {
  defaultValues?: Partial<StatusChartFilter>;
  isInGate: boolean;
  onSubmit: SubmitHandler<StatusChartFilter>;
};

const useFormSchema = () => {
  return z.object({
    period: z.nativeEnum(DashboardV2PeriodType),
    currency: z.object({
      id: z.string(),
      name: z.string(),
    }),
    gateGroup: z.optional(
      z.object({
        id: z.string(),
        name: z.string(),
        currency_id: z.optional(z.string()),
      })
    ),
    operationType: z.nativeEnum(StatusChartOperationType),
  });
};

export type StatusChartFilter = z.infer<ReturnType<typeof useFormSchema>>;

export const StatusChartFilter: FC<StatusChartFilterProps> = (props) => {
  const t = useTranslations('charts.filters');
  const tButtons = useTranslations('buttons');

  const { className, defaultValues, isInGate, onSubmit } = props;

  const form = useForm<StatusChartFilter>({
    resolver: zodResolver(useFormSchema()),
    defaultValues,
  });

  const { handleSubmit, register, setValue, watch } = form;

  const [currency, gateGroup] = watch(['currency', 'gateGroup']);

  const gateGroupsFilter = useCallback(
    (option: GateGroupOption) => {
      return !currency?.id || option.currency_id === currency.id;
    },
    [currency?.id]
  );

  useLayoutEffect(() => {
    if (currency?.id && gateGroup?.currency_id !== currency.id) {
      setValue('gateGroup', undefined);
    }
  }, [currency?.id, gateGroup?.currency_id, setValue]);

  return (
    <FormProvider {...form}>
      <form className={clsx(styles.root, className)} onSubmit={handleSubmit(onSubmit)}>
        <FormSelect {...register('period')} variant="box" label={t('period.label')} component={OperationPeriodSelect} />

        {!isInGate && (
          <>
            <FormSelect
              {...register('currency')}
              variant="box"
              label={t('currency.label')}
              component={CurrencySelect}
            />

            <FormSelect
              {...register('gateGroup')}
              variant="box"
              label={t('gateGroup.label')}
              component={GateGroupSelect}
              filter={gateGroupsFilter}
            />
          </>
        )}

        <FormSubmitButton className={styles.submitButton}>{tButtons('search')}</FormSubmitButton>
      </form>
    </FormProvider>
  );
};
